import React, {FC, useEffect, useState} from 'react';
import {toast, ToastContainer} from 'react-toastify';
import {useDispatch, useSelector} from 'react-redux';
import {AppDispatch, RootState} from '../../../../../../redux/store';
import {getTransactionListApi, postTransactionApi} from '../../../../../../redux/slices/TransactionSlice';
import Table from '../../../common/Table';
import TableHeaderTransaction from '../../../../../../../_metronic/partials/widgets/tables/TableHeaderTransaction';
import {OverlayTrigger, Tooltip, Spinner} from 'react-bootstrap';
import {exportTransactionCSV} from '../../../../../../services/ClientService';

interface TransactionRow {
  id: number;
  [key: string]: any;
}

interface SharedTransactionTableProps {
  filterStatus?: 'pending' | 'approved' | 'rejected' | 'all';
}

const SharedTransactionTable: FC<SharedTransactionTableProps> = ({filterStatus = 'all'}) => {
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchKey, setSearchKey] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [selectedRow, setSelectedRow] = useState<TransactionRow[]>([]);
  const [exporting, setExporting] = useState(false);

  const dispatch = useDispatch<AppDispatch>();

  const transactionData = useSelector(
    (state: RootState) => state.transaction.TransactionList?.data?.data || []
  );
  const transactionTotal = useSelector(
    (state: RootState) => state.transaction.TransactionList?.data?.total || 0
  );

  // Debounced search query
  useEffect(() => {
    const debounceTimeout = setTimeout(() => {
      setDebouncedSearchQuery(searchKey);
    }, 1000);
    return () => clearTimeout(debounceTimeout);
  }, [searchKey]);

  // Fetch transaction data
  const fetchTransactionList = () => {
    setLoading(true);
    dispatch(
      getTransactionListApi({
        pageNumber: currentPage,
        pageSize: rowsPerPage,
        filterkey: debouncedSearchQuery,
      })
    )
      .then(() => {
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
        toast.error('Failed to fetch transaction data');
      });
  };

  useEffect(() => {
    fetchTransactionList();
  }, [currentPage, rowsPerPage, debouncedSearchQuery]);

  // Handlers
  const handleSearchChange = (searchQuery: string) => {
    setSearchKey(searchQuery);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePerRowsChange = (newPerPage: number, page: number) => {
    setCurrentPage(page);
    setRowsPerPage(newPerPage);
  };

  const handleStatus = (id: number, status: string) => {
    const payload = {status};
    dispatch(postTransactionApi({id, data: payload}))
      .then(() => {
        toast.success('Entity updated successfully.');
        fetchTransactionList();
      })
      .catch(() => toast.error('Failed to update entity.'));
  };

  const handleExportCSV = async () => {
    setExporting(true);
    try {
      const response = await exportTransactionCSV();
      const downloadUrl = response?.data?.data?.download_url;
      const fileName = response?.data?.data?.file_name;

      if (downloadUrl) {
        // Create a temporary link and trigger download
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = fileName || 'transactions.csv';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        toast.success('CSV exported successfully');
      } else {
        toast.error('Download URL not found in response');
      }
    } catch (error: any) {
      console.error('Error exporting CSV:', error);
      const errorMessage = error?.response?.data?.reason || error?.message || 'Failed to export CSV';
      toast.error(errorMessage);
    } finally {
      setExporting(false);
    }
  };

  // Export Button Component
  const ExportButton = () => (
    <OverlayTrigger placement='bottom' overlay={<Tooltip>Export to CSV</Tooltip>}>
      <button
        className='btn btn-light border rounded-circle shadow d-flex justify-content-center align-items-center symbol symbol-40px me-5'
        onClick={handleExportCSV}
        disabled={exporting}
        style={{width: '40px', height: '40px'}}
      >
        {exporting ? (
          <Spinner animation='border' size='sm' />
        ) : (
          <i className='bi bi-file-earmark-arrow-down fs-2 text-primary pe-0'></i>
        )}
      </button>
    </OverlayTrigger>
  );

  const columnData = [
    {
      name: 'Client Details',
      cell: (row: any) => (
        <div className='d-flex flex-column py-2'>
          <div className='mb-1'>
            <i className='bi bi-person-fill text-primary me-2' />
            <span className='text-dark fw-semibold fs-7'>{row?.client_detail?.name || 'N/A'}</span>
          </div>
          <div>
            <i className='bi bi-telephone-fill text-muted me-2' />
            <span className='text-muted fw-semibold fs-7'>{row?.client_detail?.phone || 'N/A'}</span>
          </div>
        </div>
      ),
      minWidth: '200px',
      sortable: true,
    },
    {
      name: 'Transaction Details',
      cell: (row: any) => (
        <div className='d-flex flex-column py-2'>
          <div className='mb-1'>
            <i className='bi bi-gift-fill text-primary me-2' />
            <span className='text-dark fw-semibold fs-7'>{row?.gift_gallery_detail?.name || 'N/A'}</span>
          </div>
          <div className='mb-1'>
            <i className='bi bi-ticket-perforated text-muted me-2' />
            <span className='text-muted fw-semibold fs-7'>{row?.coupon_detail?.name || 'N/A'}</span>
          </div>
          <div>
            <i className='bi bi-upc text-muted me-2' />
            <span className='text-muted fw-semibold fs-7'>{row?.coupon_code || 'N/A'}</span>
          </div>
        </div>
      ),
      minWidth: '220px',
      sortable: true,
    },
    {
      name: 'Amount Details',
      cell: (row: any) => (
        <div className='d-flex flex-column py-2'>
          <div className='mb-1'>
            <i className='bi bi-wallet2 text-primary me-2' />
            <span className='text-dark fw-semibold fs-7'>Points: {row?.points || 0}</span>
          </div>
          <div className='mb-1'>
            <i className='bi bi-currency-rupee text-muted me-2' />
            <span className='text-muted fw-semibold fs-7'>Money: {row?.money || '0.00'}</span>
          </div>
          <div>
            <span
              className={`badge ${row?.action_id == 1 ? 'badge-success' : 'badge-danger'} fw-bold`}
              style={{
                padding: '4px 12px',
                fontSize: '0.7rem',
                letterSpacing: '0.3px',
              }}
            >
              {row?.action_id == 1 ? 'CREDIT' : 'DEBIT'}
            </span>
          </div>
        </div>
      ),
      minWidth: '180px',
      sortable: true,
    },
    {
      name: 'Date & Time',
      cell: (row: any) => {
        const location = row?.scanned_location_at || '-'
        const formattedDate = row?.created_at
          ? new Date(row.created_at).toLocaleDateString('en-IN', {
              day: '2-digit',
              month: 'short',
              year: 'numeric',
            })
          : '-'
        const formattedTime = row?.created_at
          ? new Date(row.created_at).toLocaleTimeString('en-IN', {
              hour: '2-digit',
              minute: '2-digit',
              second: '2-digit',
              hour12: true,
            })
          : '-'
    
        return (
          <div className='d-flex flex-column py-2'>
            <div className='mb-1 d-flex align-items-center'>
              <i className='bi bi-geo-alt-fill text-info me-2' />
              <OverlayTrigger
                placement='top'
                overlay={<Tooltip id={`tooltip-location-${location}`}>{location}</Tooltip>}
              >
                <span className='text-dark fw-semibold fs-7 text-truncate' style={{maxWidth: 180}}>
                  {location}
                </span>
              </OverlayTrigger>
            </div>
            <div className='mb-1 d-flex align-items-center'>
              <i className='bi bi-calendar-check-fill text-primary me-2' />
              <span className='text-dark fw-semibold fs-7'>{formattedDate}</span>
            </div>
            <div className='d-flex align-items-center'>
              <i className='bi bi-clock-fill text-muted me-2' />
              <span className='text-muted fw-semibold fs-7'>{formattedTime}</span>
            </div>
          </div>
        )
      },
      minWidth: '180px',
      sortable: true,
    },
    {
      name: 'Status Action',
      cell: (row: any) => {
        const status = row?.status?.toLowerCase() || '';
        const isPending = status === 'pending';

        return (
          <div className='d-flex flex-column align-items-start py-2' style={{gap: '8px'}}>
            <span
              className={`badge ${
                status === 'approved'
                  ? 'badge-success'
                  : status === 'rejected'
                  ? 'badge-danger'
                  : status === 'pending'
                  ? 'badge-warning'
                  : 'badge-secondary'
              } fw-bold`}
              style={{
                padding: '8px 16px',
                fontSize: '0.75rem',
                letterSpacing: '0.5px',
                textTransform: 'uppercase',
                minWidth: '100px',
                textAlign: 'center',
              }}
            >
              {row?.status ? row.status.charAt(0).toUpperCase() + row.status.slice(1) : 'N/A'}
            </span>

            {isPending && (
              <div className='d-flex gap-2'>
                <OverlayTrigger placement='top' overlay={<Tooltip>Approve Transaction</Tooltip>}>
                  <button
                    className='btn btn-sm'
                    onClick={(e) => {
                      e.stopPropagation();
                      handleStatus(row?.id, 'approved');
                    }}
                    style={{
                      backgroundColor: 'transparent',
                      border: 'none',
                      color: '#50cd89',
                      padding: '4px',
                    }}
                  >
                    <i className='bi bi-check-circle-fill fs-4'></i>
                  </button>
                </OverlayTrigger>

                <OverlayTrigger placement='top' overlay={<Tooltip>Reject Transaction</Tooltip>}>
                  <button
                    className='btn btn-sm'
                    onClick={(e) => {
                      e.stopPropagation();
                      handleStatus(row?.id, 'rejected');
                    }}
                    style={{
                      backgroundColor: 'transparent',
                      border: 'none',
                      color: '#f1416c',
                      padding: '4px',
                    }}
                  >
                    <i className='bi bi-x-circle-fill fs-4'></i>
                  </button>
                </OverlayTrigger>
              </div>
            )}
          </div>
        );
      },
      minWidth: '150px',
      sortable: false,
    },
  ];

  // Filter data based on filterStatus prop
  const filteredData =
    filterStatus === 'all'
      ? transactionData
      : transactionData.filter((row: any) => row.status?.toLowerCase() === filterStatus);

  return (
    <div className='card'>
      <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
        <TableHeaderTransaction
          searchKey={searchKey}
          handleSearchChange={(query) => setSearchKey(query)}
          AdditionalButtonComponent={ExportButton}
        />
      </div>
      <div className='card-body p-0 shadow-sm'>
        <Table
          data={filteredData}
          columns={columnData}
          isLoading={loading}
          handlePerRowsChange={handlePerRowsChange}
          handlePageChange={handlePageChange}
          totalRows={filteredData.length}
          showPagination={true}
          selectableRows
          onSelectedRowsChange={(selected) => {
            setSelectedRow(selected.selectedRows);
          }}
        />
      </div>
      <ToastContainer />
    </div>
  );
};

export default SharedTransactionTable;
